import React from "react";
import DaysToggle from "../../components/DaysToggle.jsx";
import AsyncPagedSelect from "../../components/AsyncPagedSelect.jsx";
import FilterSheet from "../../../../components/common/FilterSheet/FilterSheet";
import { getDepartments } from "../../../../../services/DepartmentService.js";

const fetchDepartmentPage = (page, search) => getDepartments(20, page, search);
const departmentToOption = (d) => ({ value: d.id, label: d.name });

const VIEWS = [
  { key: "overview", label: "Overview" },
  { key: "labs", label: "Labs timeline" },
  { key: "trainers", label: "Trainers timeline" },
];

// Same from/to date + from/to time filters on every view (Overview, Labs
// timeline, Trainers timeline) — floor/status are about rooms, so they show
// wherever labs are listed: the Overview and the Labs timeline. Picking a multi-day range
// on the Labs/Trainers timelines switches them into a day-by-day grid (see
// GridTimeline.jsx) instead of dropping down to a single anchor day.
//
// Nine controls, which is over the threshold instruction_ui.json sets for
// a panel that should collapse behind a button on a phone -- so this one
// is a FilterSheet, unlike the single-field strips on the other tabs.
//
// What this replaced leaned on eight inline styles, one of them a <label>
// with a visibility:hidden caption whose only job was to push the Apply
// button down into line with the fields. .pg-filters-actions is the
// kit's answer to that, and it needs no spacer.
const DashboardControls = ({
  view, onViewChange,
  fromDate, onFromDateChange,
  toDate, onToDateChange,
  fromTime, onFromTimeChange,
  toTime, onToTimeChange,
  days, onDaysChange,
  department, onDepartmentChange,
  branch, onBranchChange, branchOptions,
  floor, onFloorChange, floorOptions,
  status, onStatusChange,
  search, onSearchChange,
  onApply, applyDirty,
}) => (
  <>
    <div className="lp-tabs" role="tablist" aria-label="Dashboard views">
      {VIEWS.map((v) => (
        <button
          key={v.key}
          type="button"
          role="tab"
          aria-selected={view === v.key}
          className={`lp-tab${view === v.key ? " is-on" : ""}`}
          onClick={() => onViewChange(v.key)}
        >
          {v.label}
        </button>
      ))}
    </div>

    <FilterSheet label="Filter the dashboard" as="div">
      <div className="pg-filters-search">
        <div className="pg-field pg-field--search">
          <label className="pg-label" htmlFor="dash-search">
            Search
          </label>
          <input
            id="dash-search"
            type="search"
            className="pg-input pg-input--search"
            placeholder="Lab, trainer or batch..."
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* The timelines show a month, picked in their own bar, so the
            from/to dates are the Overview's alone. */}
        {view === "overview" && (
        <>
        <div className="pg-field pg-field--tight">
          <label className="pg-label" htmlFor="dash-from-date">
            From date
          </label>
          <input
            id="dash-from-date"
            type="date"
            className="pg-input"
            value={fromDate}
            onChange={(e) => {
              const v = e.target.value;
              onFromDateChange(v);
              if (toDate && v > toDate) onToDateChange(v);
            }}
          />
        </div>

        <div className="pg-field pg-field--tight">
          <label className="pg-label" htmlFor="dash-to-date">
            To date
          </label>
          <input
            id="dash-to-date"
            type="date"
            className="pg-input"
            min={fromDate || undefined}
            value={toDate}
            onChange={(e) => onToDateChange(e.target.value)}
          />
        </div>
        </>
        )}
      </div>

      <div className="pg-filters-row">
        <div className="pg-filters-fields">
          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="dash-from-time">
              From time
            </label>
            <input
              id="dash-from-time"
              type="time"
              className="pg-input"
              value={fromTime}
              onChange={(e) => onFromTimeChange(e.target.value)}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="dash-to-time">
              To time
            </label>
            <input
              id="dash-to-time"
              type="time"
              className="pg-input"
              value={toTime}
              onChange={(e) => onToTimeChange(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="pg-filters-row">
        <div className="pg-filters-fields">
          <div className="pg-field">
            <label className="pg-label" htmlFor="dash-days">
              Day wise
            </label>
            <div id="dash-days">
              <DaysToggle value={days} onChange={onDaysChange} />
            </div>
          </div>

          {branchOptions.length > 0 && (
            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="dash-branch">
                Branch
              </label>
              <select
                id="dash-branch"
                className="pg-select"
                value={branch}
                onChange={(e) => onBranchChange(e.target.value)}
              >
                <option value="">All branches</option>
                {branchOptions.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(view === "overview" || view === "labs") && (
            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="dash-floor">
                Floor
              </label>
              <select
                id="dash-floor"
                className="pg-select"
                value={floor}
                onChange={(e) => onFloorChange(e.target.value)}
              >
                <option value="">All floors</option>
                {floorOptions.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(view === "overview" || view === "labs") && (
            <div className="pg-field pg-field--tight">
              <label className="pg-label" htmlFor="dash-status">
                Status
              </label>
              <select
                id="dash-status"
                className="pg-select"
                value={status}
                onChange={(e) => onStatusChange(e.target.value)}
              >
                <option value="">All</option>
                <option value="free">Free</option>
                <option value="occupied">Occupied</option>
                <option value="maintenance">Out of order</option>
              </select>
            </div>
          )}

          {/* Beside Status, where the row has room for a searchable picker;
              squeezed in with the two times it had none. */}
          <div className="pg-field">
            <label className="pg-label" htmlFor="dash-dept">
              Department
            </label>
            <div id="dash-dept">
              <AsyncPagedSelect
                value={department}
                onChange={onDepartmentChange}
                fetchPage={fetchDepartmentPage}
                toOption={departmentToOption}
                placeholder="Search department..."
              />
            </div>
          </div>
        </div>

        <div className="pg-filters-actions">
          {/* Clearing the times is what returns the view to whole-day
              status, so it says that rather than being an X on the
              time box. */}
          {(fromTime || toTime) && (
            <button
              type="button"
              className="pg-btn"
              onClick={() => {
                onFromTimeChange("");
                onToTimeChange("");
              }}
            >
              Whole day
            </button>
          )}

          <button
            type="button"
            className="pg-btn pg-btn--main"
            onClick={onApply}
            title={
              applyDirty
                ? "Fetch the dashboard with these filters"
                : "Filters already applied"
            }
          >
            Apply
          </button>
        </div>
      </div>
    </FilterSheet>
  </>
);

export default DashboardControls;
